// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { HttpService } from './http.service';
import {
  UserDetailsResponse,
  UserDetailsModel,
  UserGroupsCreateRequest
} from './../../access-management/state/user-details/user-details.interfaces';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { API_KEY } from '@app/core/environment.configuration';

import { OIDCUser, IAMUser, UserUpdates } from '@app/core';

@Injectable()
export class UserService {
  url = `${this.api}/users`;
  dashboardData: any = null;

  constructor(private httpClient: HttpClient, @Inject(API_KEY) private api: string, private httpService: HttpService) {}

  public update(updatedUser: OIDCUser): Observable<UserUpdates> {
    const route = `${this.api}/user`;
    return this.httpClient.patch<OIDCUser>(route, updatedUser);
  }

  public getList(): Observable<IAMUser[]> {
    const route = `${this.api}/users`;
    return this.httpClient.get<UserListResponse>(route).pipe(map(response => response.users));
  }

  public get(username: string): Observable<UserDetailsResponse> {
    return this.httpClient
      .get<UserDetailsModel>(this.url + '/' + username)
      .pipe(map((response: UserDetailsResponse) => new UserDetailsModel(response)));
  }

  public addGroupsToUser(username: string, request: UserGroupsCreateRequest) {
    const route = `${this.api}/users/${username}/groups`;
    return this.httpClient.post(route, request);
  }

  public getUser() {
    return this.httpClient.get(`${this.api}/user`);
  }

  public getDashboardData(): Observable<DashboardResponse> {
    if (this.dashboardData) {
      return of(this.dashboardData);
    }

    return this.httpClient.get(`${this.api}/dashboard`).pipe(
      tap((response: DashboardResponse) => {
        this.dashboardData = response;
      })
    ) as Observable<DashboardResponse>;
  }

  public getUserRoles(username: string) {
    return this.httpService
      .get(this.httpService.urls.users.userRoleScopes, { urlParam: username })
      .pipe(map((response: any) => response && response.map((row: any) => ({ ...row, id: row.role_id }))));
  }

  public getUserGroups(username: string): Observable<UserGroup[]> {
    return this.httpService.get(this.httpService.urls.users.userGroups, { urlParam: username });
  }
}

interface UserListResponse {
  users: User[];
}

// Response /users
export interface User {
  id: string;
  sf_id: string;
  name: string;
  email: string;
  username: string;
  accountName: string;
  roles: UserRoleSimple[];
}
// Response  /users endpoint
export interface UserRoleSimple {
  id: string;
  name: string;
  title: string;
}

// Response User/userId
export interface UserByUsername {
  id: string;
  sf_id: string;
  user_name: string;
  first_name: string;
  last_name: string;
  account_id: string;
  account_name: string;
  email: string;
}

// Response of /users/-/groups
export interface UserGroup {
  user_group_id: string;
  group_id: string;
  username: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

interface DashboardResponse {
  Users: number;
  Roles: number;
  Policies: number;
}



// WEBPACK FOOTER //
// ./src/app/core/services/user.service.ts